<template>
  <ChecklistTemplateEditor
    v-if="editingChecklistTemplate"
    :checklist-template="editingChecklistTemplate"
    @updated="handleEditorUpdated"
    @published="handleEditorPublished"
    @aborted="handleEditorAborted"
  />
  <div v-else-if="isPreparingInternalEdit" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
    Loading checklist template editor...
  </div>
  <div v-else-if="currentChecklistTemplate" class="flex flex-col gap-2">
    <p v-if="editErrorMessage" class="text-sm text-destructive">
      {{ editErrorMessage }}
    </p>

    <Card class="w-full">
      <CardHeader class="flex flex-col gap-2">
        <div class="flex items-start justify-between gap-3">
          <div class="flex flex-col gap-1">
            <CardTitle class="text-base">
              <Badge variant="outline">
                v{{ currentChecklistTemplate.version }}
              </Badge>
              <Button variant="link" class="ml-2 px-0" @click="toggleMinimized">
                {{ checklistTemplateTitle }}
              </Button>
            </CardTitle>
            <div>
              <Badge v-if="currentChecklistTemplate.public" variant="outline" class="mr-2">
                Public
              </Badge>
              <Badge v-if="checklistTemplateStatus !== 'published'" :variant="checklistTemplateStatus === 'published' ? 'default' : 'secondary'">
                {{ checklistTemplateStatusLabel }}
              </Badge>
              <Badge v-if="hasActiveDraft" variant="outline" class="ml-2">
                Draft v{{ activeDraftVersionLabel }} in progress
              </Badge>
            </div>
            <CardDescription>
              {{ checklistTemplateDescriptionLabel }}
            </CardDescription>
          </div>
          <div class="flex items-center gap-1">
            <Button v-if="showContinueEditingDraftButton" variant="outline" size="sm" :disabled="isBusy" @click="openChecklistTemplateEditor">
              Continue editing draft
            </Button>
            <DropdownMenu v-if="showChecklistTemplateActions">
              <DropdownMenuTrigger as-child>
                <Button aria-label="Checklist template actions" size="icon-sm" variant="ghost" :disabled="isBusy">
                  <MoreHorizontal class="rotate-90" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" class="w-40">
                <DropdownMenuItem v-if="canEditChecklistTemplate" :disabled="isBusy" @click="openChecklistTemplateEditor">
                  {{ editActionLabel }}
                </DropdownMenuItem>
                <DropdownMenuItem v-if="canDuplicateChecklistTemplate" :disabled="isBusy" @click="emit('duplicate', currentChecklistTemplate)">
                  {{ isDuplicating ? duplicatingActionLabel : duplicateActionLabel }}
                </DropdownMenuItem>
                <DropdownMenuItem v-if="canDeleteChecklistTemplate" :disabled="isBusy" class="text-destructive" @click="emit('delete', currentChecklistTemplate)">
                  {{ isDeleting ? deletingActionLabel : deleteActionLabel }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon-sm" class="size-8" @click="toggleMinimized">
              <ChevronDown v-if="isMinimized" class="h-4 w-4" />
              <ChevronUp v-else class="h-4 w-4" />
              <span class="sr-only">{{ isMinimized ? 'Expand checklist template' : 'Minimize checklist template' }}</span>
            </Button>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span v-if="currentChecklistTemplate.user?.name">Owner {{ currentChecklistTemplate.user.name }}</span>
          <span v-if="currentChecklistTemplate.checklist_item_templates_count !== undefined && currentChecklistTemplate.checklist_item_templates_count !== null">
            {{ currentChecklistTemplate.checklist_item_templates_count }} item template{{ currentChecklistTemplate.checklist_item_templates_count === 1 ? '' : 's' }}
          </span>
        </div>
      </CardHeader>

      <Transition
        enter-active-class="origin-top transform-gpu transition-all duration-200 ease-out motion-reduce:transition-none"
        enter-from-class="-translate-y-1 scale-y-95 opacity-0"
        enter-to-class="translate-y-0 scale-y-100 opacity-100"
        leave-active-class="origin-top transform-gpu transition-all duration-150 ease-in motion-reduce:transition-none"
        leave-from-class="translate-y-0 scale-y-100 opacity-100"
        leave-to-class="-translate-y-1 scale-y-95 opacity-0"
      >
        <CardContent v-if="!isMinimized" class="flex flex-col gap-4">
          <ChecklistTemplateItems :checklist-template-id="currentChecklistTemplate.id" :is-editable="false" />
        </CardContent>
      </Transition>
    </Card>
  </div>
  <p v-else class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
    Checklist template not found.
  </p>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge/index.js'
import { Button } from '@/components/ui/button/index.js'
import { useAuthStore } from '@/stores/auth'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu/index.js'
import ChecklistTemplateEditor from './ChecklistTemplateEditor.vue'
import ChecklistTemplateItems from './ChecklistTemplateItems.vue'
import { useChecklistTemplatesStore } from '../../stores/checklistTemplatesStore.js'

const props = defineProps({
  checklistTemplate: {
    type: Object,
    required: true,
  },
  initiallyMinimized: {
    type: Boolean,
    default: false,
  },
  showManageActions: {
    type: Boolean,
    default: false,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
  isDuplicating: {
    type: Boolean,
    default: false,
  },
  isProcessing: {
    type: Boolean,
    default: false,
  },
  editInPlace: {
    type: Boolean,
    default: false,
  },
  initiallyEditing: {
    type: Boolean,
    default: false,
  },
  duplicateActionLabel: {
    type: String,
    default: 'Duplicate',
  },
  duplicatingActionLabel: {
    type: String,
    default: 'Duplicating...',
  },
  deleteActionLabel: {
    type: String,
    default: 'Delete',
  },
  deletingActionLabel: {
    type: String,
    default: 'Deleting...',
  },
})
const emit = defineEmits(['edit', 'duplicate', 'delete', 'updated', 'published', 'aborted'])

const authStore = useAuthStore()
const checklistTemplatesStore = useChecklistTemplatesStore(`checklist_template_${props.checklistTemplate.id}`)
const currentChecklistTemplate = ref(props.checklistTemplate)
const editingChecklistTemplate = ref(null)
const editErrorMessage = ref(null)
const isPreparingInternalEdit = ref(false)
const isStartingInternalEdit = ref(false)
const isMinimized = ref(props.initiallyMinimized)
const isBusy = computed(() => {
  return props.isDeleting || props.isDuplicating || props.isProcessing || isStartingInternalEdit.value
})
const currentUserId = computed(() => Number(authStore.user?.id ?? 0))
const isOwnedByCurrentUser = computed(() => {
  return currentUserId.value > 0 && Number(currentChecklistTemplate.value?.user_id ?? 0) === currentUserId.value
})

const checklistTemplateTitle = computed(() => {
  return currentChecklistTemplate.value.title ?? `Checklist template #${currentChecklistTemplate.value.id}`
})

const checklistTemplateStatus = computed(() => {
  return currentChecklistTemplate.value.status ?? 'draft'
})

const hasActiveDraft = computed(() => {
  const activeDraftId = Number(currentChecklistTemplate.value?.active_draft?.id ?? 0)

  return activeDraftId > 0
})

const activeDraftVersionLabel = computed(() => {
  const normalizedVersion = Number(currentChecklistTemplate.value?.active_draft?.version ?? 1)

  if (Number.isNaN(normalizedVersion)) {
    return '1.00'
  }

  return normalizedVersion.toFixed(2)
})

const editActionLabel = computed(() => {
  return hasActiveDraft.value ? 'Continue editing draft' : 'Edit'
})

const showContinueEditingDraftButton = computed(() => {
  return props.showManageActions
    && isOwnedByCurrentUser.value
    && checklistTemplateStatus.value === 'published'
    && hasActiveDraft.value
})
const canEditChecklistTemplate = computed(() => props.showManageActions && isOwnedByCurrentUser.value)
const canDeleteChecklistTemplate = computed(() => props.showManageActions && isOwnedByCurrentUser.value)
const canDuplicateChecklistTemplate = computed(() => {
  return props.showManageActions && (isOwnedByCurrentUser.value || Boolean(currentChecklistTemplate.value?.public))
})
const showChecklistTemplateActions = computed(() => {
  return canEditChecklistTemplate.value || canDeleteChecklistTemplate.value || canDuplicateChecklistTemplate.value
})

const checklistTemplateStatusLabel = computed(() => {
  const normalizedStatus = String(checklistTemplateStatus.value)

  return normalizedStatus
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
})

const checklistTemplateDescriptionLabel = computed(() => {
  const description = String(currentChecklistTemplate.value.description ?? '').trim()

  return description.length > 0 ? description : null
})

const toggleMinimized = () => {
  isMinimized.value = !isMinimized.value
}

const openChecklistTemplateEditor = async () => {
  if (!props.editInPlace) {
    emit('edit', currentChecklistTemplate.value)
    return
  }

  const checklistTemplateId = Number(currentChecklistTemplate.value?.id ?? 0)

  if (checklistTemplateId < 1 || isBusy.value) {
    return
  }

  isPreparingInternalEdit.value = true
  isStartingInternalEdit.value = true
  editErrorMessage.value = null

  try {
    const draftChecklistTemplate = await checklistTemplatesStore.beginChecklistTemplateEditing(currentChecklistTemplate.value.id)

    if (!draftChecklistTemplate) {
      return
    }

    editingChecklistTemplate.value = draftChecklistTemplate
  } catch (error) {
    editErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to edit checklist template.'
  } finally {
    isPreparingInternalEdit.value = false
    isStartingInternalEdit.value = false
  }
}

const handleEditorUpdated = (updatedChecklistTemplate) => {
  if (!updatedChecklistTemplate) {
    return
  }

  editingChecklistTemplate.value = updatedChecklistTemplate

  if (Number(currentChecklistTemplate.value?.id ?? 0) === Number(updatedChecklistTemplate.id ?? 0)) {
    currentChecklistTemplate.value = updatedChecklistTemplate
  }

  emit('updated', updatedChecklistTemplate)
}

const handleEditorPublished = (publishedChecklistTemplate) => {
  if (publishedChecklistTemplate) {
    currentChecklistTemplate.value = publishedChecklistTemplate
  }

  editingChecklistTemplate.value = null
  emit('published', publishedChecklistTemplate)
}

const handleEditorAborted = (abortPayload) => {
  const originalChecklistTemplate = abortPayload?.original_checklist_template ?? null

  if (originalChecklistTemplate) {
    currentChecklistTemplate.value = originalChecklistTemplate
  } else if (Number(currentChecklistTemplate.value?.id ?? 0) === Number(editingChecklistTemplate.value?.id ?? 0)) {
    currentChecklistTemplate.value = null
  }

  editingChecklistTemplate.value = null
  emit('aborted', abortPayload)
}

watch(() => props.initiallyMinimized, (value) => {
  isMinimized.value = value
})

watch(() => props.checklistTemplate, (value) => {
  currentChecklistTemplate.value = value

  if (Number(editingChecklistTemplate.value?.id ?? 0) === Number(value?.id ?? 0)) {
    editingChecklistTemplate.value = value
  }
})

onMounted(async () => {
  if (!props.editInPlace || !props.initiallyEditing) {
    return
  }

  await openChecklistTemplateEditor()
})
</script>
